import { Ref, useEffect, useRef, useState } from 'react';
import { Button, Drawer, TextInput } from '@dsg-tech/homefield-react';
import {
  getFeatureFlagByNameSelector,
  storeIdentifierNameSelector,
} from 'store/selectors/commonSelector';
import {
  handleModalOpenEvent,
  handleSDDLocationChangeAnalyticsEvent,
} from 'utility/analytics-events';
import { useAppDispatch, useAppSelector } from 'store/store';

import { KEY_CODES } from 'utility/constants';
import { deliveryZipSelector } from 'store/selectors/locationServicesSelector';
import { setDeliveryZip } from 'store/slices/locationServices';

import './SameDayDeliveryModal.scss';
import { setCookie } from 'utility/cookie-utils';

type Props = {
  headerRef?: Ref<any>;
};

const SameDayDeliveryModal = ({ headerRef = undefined }: Props) => {
  const dispatch = useAppDispatch();
  const storeIdentifierName = useAppSelector(storeIdentifierNameSelector);
  const sameDayDeliveryEnabled = useAppSelector(
    getFeatureFlagByNameSelector('sameDayDeliveryEnabled'),
  );
  const deliveryZip = useAppSelector(deliveryZipSelector);
  const [modalZip, setModalZip] = useState('');
  const [inputError, setInputError] = useState(false);
  const [sddModalOpen, setSddModalOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const updatingRef = useRef(false);

  const sameDayDelivery = (callback?: (value: string) => string) => {
    if (!sddModalOpen) {
      setSddModalOpen(true);
      handleModalOpenEvent(storeIdentifierName, 'Update Delivery Location');
    }
  };

  useEffect(() => {
    if (deliveryZip && deliveryZip !== modalZip) {
      setModalZip(deliveryZip);
    }
  }, [deliveryZip]);

  useEffect(() => {
    window.headerFunctions = {
      ...(window.headerFunctions && { ...window.headerFunctions }),
      sameDayDelivery,
    };
  }, []);

  useEffect(() => {
    if (!sddModalOpen) {
      updatingRef.current = false;
    }
  }, [sddModalOpen]);

  const handleUpdate = () => {
    if (/\b\d{5}\b/.test(modalZip)) {
      updatingRef.current = true;
      dispatch(setDeliveryZip(modalZip));
      setCookie('deliveryZip', modalZip);
      handleSDDLocationChangeAnalyticsEvent(modalZip);
      setInputError(false);
      setErrorMessage('');
      setSddModalOpen(false);

      // Fire event to let others know the delivery zip changed
      const evt = new CustomEvent('HeaderServices:SameDayDelivery:Change', { detail: modalZip });
      window.dispatchEvent(evt);
    } else {
      setInputError(true);
      setErrorMessage('Please enter a zip code');
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === KEY_CODES.ENTER) {
      e.preventDefault();
      handleUpdate();
    }
  };

  return (
    <Drawer
      className="sdd-modal"
      anchorElement={headerRef}
      open={sddModalOpen}
      onCloseModal={() => {
        setSddModalOpen(false);
      }}
      stickyCloseButton
      title="Update Delivery Location">
      <div className="sdd-modal-v2-content">
        <div className="sdd-modal-body hmf-p-m">
          <div className="sdd-modal-instructions hmf-subheader-m">
            <p className="hmf-m-0">
              Please enter your zipcode to check delivery availability in your area.
            </p>
          </div>
          <div className="sdd-modal-input hmf-display-flex hmf-align-items-flex-start hmf-my-m">
            <TextInput
              type="number"
              name="sdd-modal-text-input"
              value={modalZip}
              error={inputError}
              onChange={(e) => {
                setModalZip(e.currentTarget.value);
              }}
              onKeyDown={handleKeyDown}
              placeholder={modalZip || 'Enter Zip Code'}
              helperText={errorMessage}
            />
            <Button
              text="Update"
              className="sdd-modal-button hmf-m-0"
              variant="primary"
              onClick={handleUpdate}
            />
          </div>
        </div>
      </div>
    </Drawer>
  );
};

export default SameDayDeliveryModal;
